import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import apiClient from "@/lib/api-client";
import { logApiError, isNotFoundError } from "@/lib/errors";
import type { ClaimStatusTemplate } from "@/api/generated/client/models/ClaimStatusTemplate";
import type { TaskTemplate } from "@/api/generated/client/models/TaskTemplate";
import type { CreateTaskTemplateRequest } from "@/api/generated/client/models/CreateTaskTemplateRequest";
import type { User } from "@/api/generated/client/models/User";
import { useClaimPipelines } from "@/hooks/use-claim-pipelines";

export interface StatusWithTasks extends ClaimStatusTemplate {
  taskTemplates: TaskTemplate[];
}

export function useTaskSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Dialog States
  const [editingStatus, setEditingStatus] = useState<ClaimStatusTemplate | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);  // Edit Status Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<ClaimStatusTemplate | null>(null);

  // New states for separate Task Modals
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [activeTask, setActiveTask] = useState<TaskTemplate | null>(null);
  const [activeStatus, setActiveStatus] = useState<ClaimStatusTemplate | null>(null);
  const [taskMode, setTaskMode] = useState<'create' | 'edit'>('create');

  // Fetch current user
  const { data: user } = useQuery({
    queryKey: ["/api/users/me"],
    queryFn: async (): Promise<User> => {
      return await apiClient.users.getCurrentUser() as User;
    },
  });

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const { pipelines, isLoading: isLoadingPipelines, error: pipelinesError } = useClaimPipelines();
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | undefined>();
  const activePipelineId = selectedPipelineId ?? pipelines[0]?.id;

  // Fetch status templates
  const { data: statusTemplatesData, isLoading, error } = useQuery({
    queryKey: ["/api/claim-status-templates", activePipelineId],
    queryFn: async () => {
      const response = await apiClient.templates.getClaimStatusTemplates({ pipelineId: activePipelineId });
      return response;
    },
    enabled: !!user?.companyId && activePipelineId !== undefined,
  });

  const statusTemplates = useMemo(() =>
    Array.isArray(statusTemplatesData?.templates)
      ? statusTemplatesData.templates
      : [],
    [statusTemplatesData]
  );

  // Fetch tasks for all statuses
  const taskQueries = useQuery({
    queryKey: ["/api/claim-status-templates/tasks", activePipelineId, statusTemplates.map(s => s.id)],
    queryFn: async () => {
      const tasksMap: Record<number, TaskTemplate[]> = {};

      await Promise.all(
        statusTemplates.map(async (status) => {
          try {
            const response = await apiClient.templates.getClaimStatusTemplateTaskTemplates({
              id: status.id,
            });
            tasksMap[status.id] = response.tasks || [];
          } catch (error) {
            if (isNotFoundError(error)) {
              console.debug(`No tasks found for status template ${status.id} (${status.displayName})`);
            } else {
              logApiError(error, `Fetch tasks for status ${status.id}`);
            }
            tasksMap[status.id] = [];
          }
        })
      );

      return tasksMap;
    },
    enabled: statusTemplates.length > 0,
  });

  const statusTasksMap = useMemo(() => taskQueries.data || {}, [taskQueries.data]);

  // Combine statuses with their tasks
  const statusesWithTasks: StatusWithTasks[] = useMemo(() => {
    return statusTemplates.map(status => ({
      ...status,
      taskTemplates: statusTasksMap[status.id] || [],
    }));
  }, [statusTemplates, statusTasksMap]);

  // Mutations
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<ClaimStatusTemplate> }) => {
      return await apiClient.templates.updateClaimStatusTemplate({
        id,
        updateClaimStatusTemplateRequest: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claim-status-templates"] });
      toast({
        title: "Status Updated",
        description: "Status details updated successfully.",
      });
      setDialogOpen(false);
      setEditingStatus(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update status template.",
        variant: "destructive",
      });
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiClient.templates.deleteClaimStatusTemplate({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claim-status-templates"] });
      toast({
        title: "Status Deleted",
        description: "Status template has been deleted successfully.",
      });
      setDeleteDialogOpen(false);
      setStatusToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete status template.",
        variant: "destructive",
      });
    },
  });

  const createStatusMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; color: string }) => {
      const maxOrder = Math.max(0, ...statusTemplates.map(s => s.displayOrder || 0));
      return await apiClient.templates.createClaimStatusTemplate({
        createClaimStatusTemplateRequest: {
          pipelineId: activePipelineId!,
          displayName: data.name,
          description: data.description || null,
          color: data.color,
          displayOrder: maxOrder + 1,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claim-status-templates", activePipelineId] });
      toast({
        title: "Status Created",
        description: "New status template has been created successfully.",
      });
      setCreateDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to create status template.",
        variant: "destructive",
      });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: async ({ statusId, data }: { statusId: number; data: CreateTaskTemplateRequest }) => {
      return await apiClient.templates.createClaimStatusTemplateTaskTemplate({
        id: statusId,
        createTaskTemplateRequest: data,
      });
    },
    onSuccess: async () => {
      // Force refetch of all status template related queries
      await queryClient.refetchQueries({ queryKey: ["/api/claim-status-templates"] });
      await queryClient.refetchQueries({ queryKey: ["/api/claim-status-templates/tasks"] });

      toast({ title: "Task Created", description: "New task added successfully." });
      setTaskDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Failed to Create Task", description: error.message, variant: "destructive" });
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ statusId, taskId, data }: { statusId: number; taskId: number; data: Partial<TaskTemplate> }) => {
      return await apiClient.templates.updateClaimStatusTemplateTaskTemplate({
        id: statusId,
        taskId,
        updateTaskTemplateRequest: data,
      });
    },
    onSuccess: async () => {
      // Invalidate specific query if possible or just nuclear option
      // Nuclear option: refetch everything related to templates
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["/api/claim-status-templates"] }),
        queryClient.refetchQueries({ queryKey: ["/api/claim-status-templates/tasks"] })
      ]);
      toast({ title: "Task Updated", description: "Task updated successfully." });
      setTaskDialogOpen(false);
    },
    onError: (error: Error) => {
      console.error('Task update failed:', error);
      toast({ title: "Failed to Update Task", description: error.message, variant: "destructive" });
    }
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async ({ statusId, taskId }: { statusId: number; taskId: number }) => {
      return await apiClient.templates.deleteClaimStatusTemplateTaskTemplate({
        id: statusId,
        taskId,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["/api/claim-status-templates"] }),
        queryClient.refetchQueries({ queryKey: ["/api/claim-status-templates/tasks"] })
      ]);
      toast({ title: "Task Deleted", description: "Task removed successfully." });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to Delete Task", description: error.message, variant: "destructive" });
    }
  });

  // Handlers for Status operations
  const handleEditStatus = (status: ClaimStatusTemplate) => {
    setEditingStatus(status);
    setDialogOpen(true);
  };

  const handleSaveStatus = async (statusToSave?: ClaimStatusTemplate) => {
    // Prefer passed status, fallback to state
    const targetStatus = statusToSave || editingStatus;

    if (!targetStatus) {
      console.error('handleSaveStatus: No status to save');
      return;
    }

    updateStatusMutation.mutate({
      id: targetStatus.id,
      data: {
        displayName: targetStatus.displayName,
        description: targetStatus.description,
        color: targetStatus.color,
        displayOrder: targetStatus.displayOrder,
      },
    });
  };

  const handleDeleteStatus = () => {
    if (statusToDelete) deleteStatusMutation.mutate(statusToDelete.id);
  };

  const handleOpenDeleteDialog = (status: ClaimStatusTemplate) => {
    setStatusToDelete(status);
    setDeleteDialogOpen(true);
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateStatus = async (data: { name: string; description: string; color: string }) => {
    await createStatusMutation.mutateAsync(data);
  };

  const handleReorderStatuses = async (reorderedStatuses: StatusWithTasks[]) => {
    // Keep existing implementation details for reorder...
    try {
      const updatePromises = reorderedStatuses.map(status =>
        apiClient.templates.updateClaimStatusTemplate({
          id: status.id,
          updateClaimStatusTemplateRequest: { displayOrder: status.displayOrder },
        })
      );
      await Promise.all(updatePromises);
      await queryClient.invalidateQueries({ queryKey: ["/api/claim-status-templates"] });
      toast({ title: "Order Updated", description: "Status order updated." });
    } catch (error) {
      // ... existing error handling
      logApiError(error, "Update status order");
    }
  };

  // Handlers for Task operations
  const handleOpenCreateTask = (status: ClaimStatusTemplate) => {
    setActiveStatus(status);
    setActiveTask(null);
    setTaskMode('create');
    setTaskDialogOpen(true);
  };

  const handleOpenEditTask = (task: TaskTemplate, status: ClaimStatusTemplate) => {
    setActiveStatus(status);
    setActiveTask(task);
    setTaskMode('edit');
    setTaskDialogOpen(true);
  };

  const handleSaveTask = (taskData: Partial<TaskTemplate>) => {
    if (!activeStatus) {
      console.error('handleSaveTask: No active status');
      return;
    }

    if (taskMode === 'create') {
      createTaskMutation.mutate({
        statusId: activeStatus.id,
        data: {
          title: taskData.title!,
          description: taskData.description || undefined,
          priority: taskData.priority || "medium",
          dueDaysOffset: taskData.dueDaysOffset || null,
          isRequired: taskData.isRequired || false,
          displayOrder: (statusTasksMap[activeStatus.id]?.length || 0) + 1,
        }
      });
    } else if (taskMode === 'edit' && activeTask) {
      updateTaskMutation.mutate({
        statusId: activeStatus.id,
        taskId: activeTask.id,
        data: taskData
      });
    } else {
      console.error('handleSaveTask: Invalid mode or missing activeTask', { taskMode, activeTask });
    }
  };

  const handleDeleteTask = (task: TaskTemplate, status: ClaimStatusTemplate) => {
    if (confirm('Are you sure you want to delete this task?')) {
      deleteTaskMutation.mutate({ statusId: status.id, taskId: task.id });
    }
  };

  return {
    // Data
    user,
    statusesWithTasks,
    isLoading: isLoading || isLoadingPipelines,
    pipelines,
    selectedPipelineId: activePipelineId,
    setSelectedPipelineId,
    error: error ?? pipelinesError,
    hasCompany: !!user?.companyId,
    isAdmin,

    // Status Dialog state
    dialogOpen,
    deleteDialogOpen,
    createDialogOpen,
    editingStatus,
    statusToDelete,

    // Task Dialog state
    taskDialogOpen,
    activeTask,
    taskMode,

    // Mutation states
    isDeleting: deleteStatusMutation.isPending,
    isCreating: createStatusMutation.isPending,
    isStatusUpdating: updateStatusMutation.isPending,
    isTaskSaving: createTaskMutation.isPending || updateTaskMutation.isPending,

    // Actions
    handleEditStatus,
    handleSaveStatus,
    handleDeleteStatus,
    handleOpenDeleteDialog,
    handleOpenCreateDialog,
    handleCreateStatus,
    handleReorderStatuses,

    // Task Actions
    handleOpenCreateTask,
    handleOpenEditTask,
    handleSaveTask,
    handleDeleteTask,

    setEditingStatus,
    setDialogOpen,
    setDeleteDialogOpen,
    setCreateDialogOpen,
    setTaskDialogOpen,
  };
}
